import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiError } from "../../../utils/api/api.service";
import { sellerService } from "../services/seller.service";
import { Product } from "../../products/interfaces/products.interface";
import {
  SellerProfileData,
  SellerProfileReportPayload,
  SellerReviewItem,
  SellerReviewUser,
  SellerReviewsSummary,
} from "../interfaces/seller.interface";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message || "An unexpected error occurred";
  return "An unexpected error occurred";
}

/**
 * Resolve seller identifier: if it's a UUID return it, otherwise call API to resolve slug to id.
 */
export const resolveSellerId = createAsyncThunk<
  string | null,
  string,
  { rejectValue: string }
>("seller/resolveSellerId", async (slugOrId, { rejectWithValue }) => {
  if (!slugOrId) return null;
  if (UUID_REGEX.test(slugOrId)) return slugOrId;
  try {
    const response = await sellerService.resolveSellerId(slugOrId);
    if (response?.success && response?.data?.id) return response.data.id;
    return null;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Fetch seller profile by user id.
 */
export const fetchSellerProfile = createAsyncThunk<
  SellerProfileData | null,
  string,
  { rejectValue: string }
>("seller/fetchSellerProfile", async (userId, { rejectWithValue }) => {
  try {
    const response = await sellerService.getSellerProfile(userId);
    if (response?.success && response?.data) return response.data;
    return null;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Fetch seller's products by user id.
 */
export const fetchSellerProducts = createAsyncThunk<
  Product[],
  string,
  { rejectValue: string }
>("seller/fetchSellerProducts", async (userId, { rejectWithValue }) => {
  try {
    const response = await sellerService.getSellerProducts(userId);
    if (!response?.data) return [];
    const data = response.data as { products?: Product[] } | Product[];
    if (Array.isArray(data)) return data;
    if (data && typeof data === "object" && Array.isArray((data as { products?: Product[] }).products)) {
      return (data as { products: Product[] }).products;
    }
    const arr = Object.values(data as object).find((v): v is Product[] => Array.isArray(v) && v.length > 0);
    return arr ?? [];
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Fetch seller reviews by user id. Returns normalized reviews and summary.
 */
export const fetchSellerReviews = createAsyncThunk<
  { reviews: SellerReviewItem[]; summary: SellerReviewsSummary },
  string,
  { rejectValue: string }
>("seller/fetchSellerReviews", async (userId, { rejectWithValue }) => {
  try {
    const response = await sellerService.getSellerReviews(userId);
    const data = response?.data;
    const rawReviews = Array.isArray(data)
      ? data
      : data?.reviews ?? [];
    const reviews: SellerReviewItem[] = rawReviews.map((r: {
      id: string;
      rating: number;
      comment: string | null;
      createdAt: string;
      productId?: string;
      productTitle?: string;
      reviewer?: SellerReviewUser;
      user?: SellerReviewUser;
      helpfulYesCount?: number;
      helpfulNoCount?: number;
      userVote?: "yes" | "no" | null;
    }) => ({
      id: r.id,
      rating: r.rating,
      comment: r.comment ?? null,
      createdAt: r.createdAt,
      productId: r.productId ?? "",
      productTitle: r.productTitle ?? "",
      user: r.reviewer ?? r.user ?? { id: "", firstName: null, lastName: null, profileImage: null },
      helpfulYes: r.helpfulYesCount ?? 0,
      helpfulNo: r.helpfulNoCount ?? 0,
      userVote: r.userVote ?? null,
    }));
    const summary: SellerReviewsSummary = {
      averageRating: (data && !Array.isArray(data)) ? data.averageRating ?? 0 : response?.metadata?.averageRating ?? 0,
      totalReviews: (data && !Array.isArray(data)) ? data.totalReviews ?? 0 : response?.metadata?.totalReviews ?? response?.pagination?.total ?? reviews.length,
      ratingDistribution: (data && !Array.isArray(data)) ? data.ratingDistribution ?? {} : response?.metadata?.ratingDistribution ?? {},
    };
    return { reviews, summary };
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Post a review for a seller.
 */
export const postSellerReview = createAsyncThunk<
  void,
  { userId: string; rating: number; comment: string },
  { rejectValue: string }
>("seller/postSellerReview", async ({ userId, rating, comment }, { rejectWithValue }) => {
  try {
    const response = await sellerService.postSellerReview(userId, { rating, comment });
    if (response?.success) return;
    return rejectWithValue("Failed to post review");
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Submit helpfulness vote for a review. Returns server counts for the review and user's current vote.
 */
export const submitReviewHelpfulness = createAsyncThunk<
  { reviewId: string; helpfulYes: number; helpfulNo: number; userVote: "yes" | "no" | null },
  { reviewId: string; isHelpful: boolean },
  { rejectValue: string }
>("seller/submitReviewHelpfulness", async ({ reviewId, isHelpful }, { rejectWithValue }) => {
  try {
    const response = await sellerService.submitReviewHelpfulness(reviewId, isHelpful);
    const data = response?.data as { helpfulYesCount?: number; helpfulNoCount?: number; userVote?: "yes" | "no" | null } | undefined;
    return {
      reviewId,
      helpfulYes: data?.helpfulYesCount ?? 0,
      helpfulNo: data?.helpfulNoCount ?? 0,
      userVote: data?.userVote ?? null,
    };
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Report a seller profile.
 */
export const reportSellerProfile = createAsyncThunk<
  void,
  { userId: string } & SellerProfileReportPayload,
  { rejectValue: string }
>("seller/reportSellerProfile", async ({ userId, ...payload }, { rejectWithValue }) => {
  try {
    const response = await sellerService.reportSellerProfile(userId, payload);
    if (response?.success) return;
    return rejectWithValue("Failed to submit report");
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});
